/**
 * Browser GramJS operations for the web-based group setup flow.
 *
 * MTProto stays in the browser — the server never holds a live Telegram session.
 * These functions are called by the setup wizard UI to carry out account-level
 * Telegram operations the bot cannot perform: create a supergroup, add + promote
 * the bot, invite contacts, and join via invite link.
 */

import { TelegramClient } from "telegram";
import { Api } from "telegram";
import { apiFetch } from "./api.js";
import { botUsername } from "./util.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SetupGroupResult {
  ok: boolean;
  group_id: string;
  error?: string;
}

export interface ContactEntry {
  id: string;          // user id as string
  accessHash: string;  // needed for InputUser when adding to group
  name: string;        // full display name (first + last)
  username?: string;
  phone?: string;
  canAdd: boolean;     // false if USER_PRIVACY_RESTRICTED
}

// ---------------------------------------------------------------------------
// Step 1: Create supergroup + promote bot
// ---------------------------------------------------------------------------

/**
 * Supergroups use a distinct id convention from basic groups: Bot-API id is
 * `-100<mtprotoId>` (a string prefix), not bare negation. Getting this wrong
 * silently produces a chat_id that resolves to nothing.
 */
export function supergroupChatId(mtprotoId: number): number {
  return Number(`-100${mtprotoId}`);
}

export function supergroupMtprotoId(chatId: number): number {
  return Number(String(chatId).replace(/^-100/, ""));
}

/**
 * Create a fresh supergroup (megagroup) with just the founder in it. A
 * supergroup is the terminal state basic groups migrate *into* — creating one
 * directly means there is no basic-group state left to silently migrate out
 * of later (see docs/superpowers/specs/2026-07-07-supergroup-only-design.md).
 * Returns the new chat id (negative Bot-API id, `-100...` form).
 */
export async function createSupergroup(
  client: TelegramClient,
  groupName: string,
): Promise<number> {
  const result = await client.invoke(
    new Api.channels.CreateChannel({
      megagroup: true,
      title: groupName,
      about: "",
    }),
  );
  const chats = (result as any).chats;
  if (!chats || chats.length === 0) {
    throw new Error("CreateChannel returned no chat");
  }
  const mtprotoId = Number(chats[0].id);
  return supergroupChatId(mtprotoId);
}

/**
 * Resolve the bot, add it to the group, and promote it with the rights
 * needed for snail enforcement (delete + restrict + invite).
 *
 * @param chatId Bot-API chat id (`-100...` for supergroups)
 */
export async function promoteBotInGroup(
  client: TelegramClient,
  chatId: number,
  botUser: string = botUsername(),
): Promise<void> {
  const botPeer = await client.invoke(
    new Api.contacts.ResolveUsername({ username: botUser.replace("@", "") }),
  );

  const mtprotoChatId = supergroupMtprotoId(chatId);

  const botInputUser = (botPeer as any).users?.[0];
  if (!botInputUser) throw new Error("Could not resolve bot user");

  try {
    await client.invoke(
      new Api.channels.InviteToChannel({
        channel: mtprotoChatId as any,
        users: [botInputUser as any],
      }),
    );
  } catch (e: any) {
    // Retry-safe: an earlier half-failed run may have added the bot but not
    // promoted it. Already-in-group is fine — proceed to EditAdmin.
    if (!String(e?.message || e).includes("USER_ALREADY_PARTICIPANT")) throw e;
  }

  // Unlike basic-group EditChatAdmin (all-or-nothing isAdmin flag),
  // channels.EditAdmin takes explicit per-right flags — grant exactly what
  // snail's group-silence enforcement needs.
  await client.invoke(
    new Api.channels.EditAdmin({
      channel: mtprotoChatId as any,
      userId: botInputUser as any,
      adminRights: new Api.ChatAdminRights({
        deleteMessages: true,
        banUsers: true,
        inviteUsers: true,
      }),
      rank: "",
    }),
  );
}

/**
 * Report step 1 completion to the server.
 */
export async function reportStep1(
  groupId: string,
  telegramChatId: number,
  groupName: string,
): Promise<void> {
  await apiFetch(`/setup/${groupId}/step1`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      telegram_chat_id: telegramChatId,
      group_name: groupName,
    }),
  });
}

// ---------------------------------------------------------------------------
// Step 2: Contacts picker + invite
// ---------------------------------------------------------------------------

/**
 * Fetch the founder's Telegram contacts for the multi-select picker.
 */
export async function getContacts(
  client: TelegramClient,
): Promise<ContactEntry[]> {
  const result = await client.invoke(
    new Api.contacts.GetContacts({ hash: 0 as any }),
  );
  const contacts: ContactEntry[] = [];
  const users = (result as any).users || [];
  for (const user of users) {
    if (!user || (user as any).deleted) continue;
    const u = user as any;
    const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ");
    contacts.push({
      id: String(u.id),
      accessHash: String(u.accessHash ?? "0"),
      name: fullName || u.username || String(u.id),
      username: u.username || undefined,
      phone: u.phone || undefined,
      canAdd: true,
    });
  }
  return contacts;
}

/**
 * Add multiple contacts to the group in one batched call. Returns list of
 * user ids that failed (privacy-restricted or otherwise unreachable).
 *
 * @param chatId Bot-API chat id (`-100...` for supergroups)
 */
export async function addContactsToGroup(
  client: TelegramClient,
  chatId: number,
  contacts: ContactEntry[],
): Promise<string[]> {
  const mtprotoChatId = supergroupMtprotoId(chatId);
  const users = contacts.map(
    (c) =>
      new Api.InputUser({
        userId: c.id as any,
        accessHash: c.accessHash as any,
      }),
  );
  const result = await client.invoke(
    new Api.channels.InviteToChannel({
      channel: mtprotoChatId as any,
      users: users as any,
    }),
  );
  const missing = (result as any).missingInvitees ?? [];
  const missingIds = new Set(missing.map((m: any) => String(m.userId)));
  return contacts.filter((c) => missingIds.has(c.id)).map((c) => c.id);
}

// ---------------------------------------------------------------------------
// Snail invite link join flow
// ---------------------------------------------------------------------------

/**
 * Resolve a snail join token to get the underlying Telegram invite URL.
 */
export async function resolveSnailJoinToken(
  token: string,
): Promise<{ group_id: string; telegram_invite_url: string }> {
  const res = await fetch(`/api/join/${token}`);
  if (!res.ok) {
    if (res.status === 410) throw new Error("Link expired");
    // 409 carries a human-readable refusal ("this circle is full").
    const detail = (await res.json().catch(() => null))?.detail;
    throw new Error(detail || `Join error ${res.status}`);
  }
  return res.json();
}

/**
 * Import a Telegram invite link (the hidden raw link behind the snail token)
 * to join the group. Then send /start g_<gid> to the bot to complete onboarding.
 */
export async function joinViaSnailLink(
  client: TelegramClient,
  snailToken: string,
): Promise<void> {
  const { group_id, telegram_invite_url } = await resolveSnailJoinToken(snailToken);

  await client.invoke(
    new Api.messages.ImportChatInvite({
      hash: extractInviteHash(telegram_invite_url),
    }),
  );

  const botPeer = await client.getInputEntity(botUsername());
  await client.sendMessage(botPeer, {
    message: `/start g_${group_id}`,
  });
}

/**
 * Extract the invite hash from a t.me/+ URL (the part after the +).
 */
function extractInviteHash(url: string): string {
  const m = url.match(/(?:\+|joinchat\/)([A-Za-z0-9_-]+)/);
  if (!m) throw new Error("Invalid invite URL: " + url);
  return m[1];
}

// ---------------------------------------------------------------------------
// Web API wrappers (not GramJS)
// ---------------------------------------------------------------------------

export interface ClaimParams {
  token?: string;
  displayName: string;
  email?: string;
  groupName: string;
  publicationHourUtc: number;
  cadenceWeeks?: number;
}

/** The POST /api/setup/claim request body. Pure — no fetch — so it's
 * unit-testable on its own. `token` is omitted entirely (not sent as null)
 * when absent: the server mints one server-side and tags it origin="web". */
export function buildClaimBody(params: ClaimParams): Record<string, unknown> {
  const body: Record<string, unknown> = {
    display_name: params.displayName,
    group_name: params.groupName,
    publication_hour_utc: params.publicationHourUtc,
    cadence_weeks: params.cadenceWeeks || 2,
  };
  if (params.email) body.email_address = params.email;
  if (params.token) body.token = params.token;
  return body;
}

export async function claimSetupToken(
  params: ClaimParams,
): Promise<SetupGroupResult> {
  return apiFetch("/setup/claim", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(buildClaimBody(params)),
  });
}

export interface SetupStatus {
  group_id: string;
  step: "promote" | "invite" | "finalized";
  group_name: string;
  telegram_chat_id: number | null;
}

export async function getSetupStatus(groupId: string): Promise<SetupStatus> {
  return apiFetch(`/setup/${groupId}/status`);
}

export async function mintJoinLink(
  groupId: string,
): Promise<{ ok: boolean; snail_token?: string; error?: string }> {
  return apiFetch(`/setup/${groupId}/join-link`, { method: "POST" });
}

export async function finalizeSetup(groupId: string): Promise<{ ok: boolean }> {
  return apiFetch(`/setup/${groupId}/finalize`, { method: "POST" });
}

export async function patchSetup(
  groupId: string,
  fields: {
    group_name?: string;
    publication_hour_utc?: number;
    cadence_weeks?: number;
    email_address?: string;
  },
): Promise<{ ok: boolean }> {
  return apiFetch(`/setup/${groupId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(fields),
  });
}